import { TimeframeObject } from "./TimeframeObject";
import { Dispatch, SetStateAction, useState } from "react";
import { DateTime, Interval } from "luxon";
import {
  _throw,
  IllegalStateError,
  UnsupportedError,
} from "shared-ts-utils-core";
import {
  IntervalSetting,
  useUserSettingWithDefault,
} from "shared-ts-utils-user-settings";
import { UseTimeframeInput } from "./useTimeframe";
import { TimeframeUtils } from "./TimeframeUtils";
import { resolveDefaultTimeframeOptions } from "./resolveDefaultTimeframeOptions";
import { TimeframeUnit } from "./TimeframeUnit";

export const useTimeframeState = (
  input: UseTimeframeInput
): [TimeframeObject, Dispatch<SetStateAction<TimeframeObject>>] => {
  const { start, end, ...partialOptions } = input;

  const defaultInterval = resolveInitialInterval(
    resolveDefaultTimeframeOptions(partialOptions),
    start != null && end != null
      ? ((Interval.fromDateTimes(start, end) ??
          _throw(new IllegalStateError("Invalid Interval"))) as Interval<true>)
      : undefined
  );
  if (partialOptions.persistUsingUserSettings) {
    const userSettingName =
      partialOptions.storageId != null
        ? typeof partialOptions.storageId === "string"
          ? partialOptions.storageId + "timeframe"
          : partialOptions.storageId()
        : "timeframe";
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useUserSettingWithDefault(
      userSettingName,
      IntervalSetting,
      defaultInterval
    ) as [TimeframeObject, Dispatch<SetStateAction<TimeframeObject>>];
  } else if (partialOptions.persistUsingQueryParams) {
    throw new UnsupportedError("persistUsingQueryParams is not supported yet");
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useState<TimeframeObject>(
      resolveInitialTimeframe(
        resolveDefaultTimeframeOptions(partialOptions),
        start != null && end != null
          ? ((Interval.fromDateTimes(start, end) ??
              _throw(
                new IllegalStateError("Invalid Interval")
              )) as Interval<true>)
          : undefined
      )
    );
  }
};

const resolveInitialTimeframe = (
  options: {
    unit: TimeframeUnit;
    maxEnd?: DateTime<true>;
    disallowFuture: boolean;
  },
  defaultTimeframe?: TimeframeObject
): TimeframeObject => {
  if (defaultTimeframe != null) {
    return defaultTimeframe;
  }
  return TimeframeUtils.resolveNowTimeframe(options);
};

const resolveInitialInterval = (
  options: {
    unit: TimeframeUnit;
    maxEnd?: DateTime;
    disallowFuture: boolean;
  },
  defaultTimeframe?: TimeframeObject
): Interval<true> => {
  if (defaultTimeframe != null) {
    return defaultTimeframe;
  }
  return TimeframeUtils.resolveNowTimeframe(options);
};
